import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Play, Trash2, Trophy, Beaker } from "lucide-react";

export default function EditionsManager() {
  const [editions, setEditions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    start_date: "",
    end_date: "",
    onboarding_start_date: "",
    is_beta: false,
  });
  const [saving, setSaving] = useState(false);

  const refresh = () => api.get("/editions").then((r) => setEditions(r.data));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Define o nome da edição"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Define datas de início e fim"); return; }
    if (form.end_date < form.start_date) { toast.error("Data de fim deve ser igual ou posterior à de início"); return; }
    setSaving(true);
    try {
      await api.post("/editions", {
        name: form.name.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        onboarding_start_date: form.onboarding_start_date || null,
        is_beta: !!form.is_beta,
      });
      toast.success("Edição criada");
      setCreating(false);
      setForm({ name: "", start_date: "", end_date: "", onboarding_start_date: "", is_beta: false });
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const activate = async (e) => {
    if (!window.confirm(
      `Iniciar "${e.name}" como edição actual?\n\n` +
      `• Os pontos da edição corrente serão arquivados (não eliminados).\n` +
      `• A semana actual volta para 1.\n` +
      `• Os rankings/MVPs passam a contar apenas esta nova edição.\n\n` +
      `Confirma para avançar.`
    )) return;
    try {
      await api.post(`/editions/${e.id}/activate`, {});
      toast.success(`Edição "${e.name}" agora a decorrer`);
      refresh();
      // Refresh page so /settings também actualiza com novos dados de edição
      setTimeout(() => window.location.reload(), 600);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Eliminar definitivamente "${e.name}"? (Só permitido se a edição não tiver entries.)`)) return;
    try {
      await api.delete(`/editions/${e.id}`);
      toast.success("Edição eliminada");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="kw-card p-6 max-w-3xl" data-testid="editions-manager">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Trophy className="w-5 h-5 text-[#E50000]" />
          <div>
            <div className="kw-overline">Phase B · Núcleo</div>
            <h2 className="font-display text-xl font-black uppercase">Edições da Liga</h2>
          </div>
        </div>
        <button onClick={() => setCreating(!creating)} className="kw-btn-secondary text-xs inline-flex items-center gap-1.5"
          data-testid="editions-toggle-form">
          <Plus className="w-3 h-3" /> {creating ? "Cancelar" : "Nova Edição"}
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-[#0A0A0A] p-4 mb-4 border border-[#222] space-y-3" data-testid="editions-form">
          <div>
            <label className="kw-overline block mb-1 text-[10px]">Nome da edição</label>
            <input className="kw-input" placeholder="Ex: Edição #1 — Primavera 2027"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edition-name-input" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="kw-overline block mb-1 text-[10px]">Data de início</label>
              <input type="date" className="kw-input" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="edition-start-input" />
            </div>
            <div>
              <label className="kw-overline block mb-1 text-[10px]">Data de fim</label>
              <input type="date" className="kw-input" value={form.end_date} min={form.start_date || undefined}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                data-testid="edition-end-input" />
            </div>
          </div>
          <div>
            <label className="kw-overline block mb-1 text-[10px]">Onboarding — início da janela de candidaturas (opcional)</label>
            <input type="date" className="kw-input" value={form.onboarding_start_date}
              onChange={(e) => setForm({ ...form, onboarding_start_date: e.target.value })}
              data-testid="edition-onboarding-input" />
            <p className="text-[10px] text-[#737373] mt-1">
              A partir desta data, os pontos acumulados pelos consultores em modo &quot;bench&quot; contam para a qualificação à próxima edição.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.is_beta}
              onChange={(e) => setForm({ ...form, is_beta: e.target.checked })}
              data-testid="edition-beta-input" />
            <span className="text-[#A3A3A3]">Marcar como edição de teste (Beta) — não conta para rankings oficiais</span>
          </label>
          <div className="flex justify-end">
            <button className="kw-btn-primary text-sm" disabled={saving} data-testid="edition-save">
              {saving ? "A criar…" : "Criar Edição"}
            </button>
          </div>
        </form>
      )}

      {editions.length === 0 && <div className="text-[#737373] text-sm">Sem edições.</div>}
      <div className="space-y-2">
        {editions.map((e) => (
          <div key={e.id}
            className={`p-3 border flex items-center justify-between gap-3 ${
              e.is_current ? "border-[#E50000] bg-[#E50000]/5" : "border-[#222] bg-[#0A0A0A]"
            }`}
            data-testid={`edition-row-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{e.name}</span>
                {e.is_current && <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold bg-[#E50000] text-white">A decorrer</span>}
                {e.is_beta && (
                  <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold border border-[#FFB020] text-[#FFB020] inline-flex items-center gap-1">
                    <Beaker className="w-3 h-3" /> Beta
                  </span>
                )}
                {!e.is_current && e.status === "closed" && (
                  <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold text-[#737373] border border-[#333]">Fechada</span>
                )}
                {!e.is_current && e.status === "upcoming" && (
                  <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold text-[#3B82F6] border border-[#3B82F6]">Por iniciar</span>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                {e.start_date} → {e.end_date}
                {e.onboarding_start_date && <> · Onboarding desde {e.onboarding_start_date}</>}
                {" · "}{e.entry_count} entrada{e.entry_count === 1 ? "" : "s"}
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              {!e.is_current && (
                <button onClick={() => activate(e)}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-[#E50000] px-2 py-1 inline-flex items-center gap-1"
                  data-testid={`edition-activate-${e.id}`}>
                  <Play className="w-3 h-3" /> Iniciar
                </button>
              )}
              {!e.is_current && e.entry_count === 0 && (
                <button onClick={() => remove(e)}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#FF6B6B] hover:text-white px-2 py-1 inline-flex items-center gap-1"
                  data-testid={`edition-delete-${e.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-[#737373] border-t border-[#222] pt-3 mt-4">
        💡 Os pontos de cada edição ficam isolados. Quando uma nova edição é activada, os rankings e MVPs voltam a zero — mas o histórico fica preservado para consulta. O reset por aniversário individual NÃO afeta os baldes de Edição.
      </p>
    </div>
  );
}
